import React, {useEffect, useState} from "react";
import {Input, List, Popover, Spin} from "antd";
import {CloseCircleFilled} from '@ant-design/icons';
import axios from "axios";
import {SuitableVehicle} from "../../../../../../models/articles/catalog/search";
import "./ui.input.widget.vin.scss"
import {useTranslation} from "react-i18next";
import {articlesService} from "../../../../../../services/articles/articles.service";

interface UIInputWidgetSearchProps {
    value?: string;
    onChange?: (value?: string) => void;
}

const parseValue = (value: string): {VIN: string, vehicleId: number, valid: boolean} => {
    let result = {
        VIN: "",
        vehicleId: 0,
        valid: false
    }

    const parts = value.split("##");
    if (parts.length !== 2) {
        return result;
    }

    const vin = parts[0].trim();
    const idPart = parts[1].trim();

    if (!vin || !idPart) {
        return result;
    }

    const vehicleId = Number(idPart);
    if (isNaN(vehicleId)) {
        return result;
    }

    result.valid = true;
    result.VIN = vin;
    result.vehicleId = vehicleId;

    return result;
}

export const UIInputWidgetVIN: React.FC<UIInputWidgetSearchProps> = ({value, onChange}) => {

    const {t} = useTranslation('translation', {useSuspense: false});

    const [vin, setVIN] = useState<string>("");
    const [vehicleID, setVehicleID] = useState<number>(-1);
    const [loading, setLoading] = useState<boolean>(false);

    const [vehicles, setVehicles] = useState<SuitableVehicle[]>([]);
    const [resultsShown, setResultsShown] = useState<boolean>(false);

    useEffect(() => {
        const parsed = parseValue(String(value))
        if (parsed.valid) {
            setVIN(parsed.VIN);
            setVehicleID(parsed.vehicleId)
        }
    }, [value]);

    const onPressEnter = async () => {
        return onSearch(vin);
    }

    const onSearch = async (value: string) => {

        if (!value) {
            if (onChange) {
                onChange(undefined)
                setResultsShown(false)
                setVehicles([])
                return
            }
        }

        setLoading(true);
        setResultsShown(false)
        try {
            const result = await articlesService.searchByVINCode(value)
            if (result.length === 1) {
                if (onChange) {
                    onChange(vin + "##" + String(result[0].id))
                    return;
                }
            }

            setVehicles(result);
            setResultsShown(true);
        } finally {
            setLoading(false);
        }
    }

    const onSelectVehicle = async (vehicle: SuitableVehicle) => {
        setResultsShown(false)
        if (onChange) {
            setVehicleID(vehicle.id)
            onChange(vin + "##" + String(vehicle.id))
        }
    }

    const onVisibleChange = (visible: boolean) => {
        setResultsShown(visible && vehicles.length > 0)
    }

    return (
        <Popover
            content={
                <div style={{maxHeight: 300, overflowY: "auto", width: 320}}>
                    {loading ? (
                        <div style={{textAlign: "center", padding: 20}}>
                            <Spin/>
                        </div>
                    ) : vehicles.length ? (
                        <List
                            dataSource={vehicles}
                            renderItem={(item) => (
                                <List.Item style={{alignItems: "flex-start"}} onClick={() => onSelectVehicle(item)}>
                                    <div className={"vin-title"}>{item.brand} {item.model}</div>
                                    <div className={"vin-attributes"}>{item.attributes.map(a => a.label + ': ' + a.value).join(', ')}</div>
                                </List.Item>
                                )}
                        />
                    ) : (
                        <div style={{padding: 16, color: "#999"}}>{t("NO_RESULTS")}</div>
                    )}
                </div>
            }
            open={resultsShown}
            placement="bottomLeft"
            overlayClassName="vin-search-popover"
            trigger="click"
            onOpenChange={onVisibleChange}
            getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
        >
            <Input.Search
                loading={loading}
                disabled={loading}
                placeholder={"VIN Number (17 digits)"}
                value={vin}
                onChange={v => setVIN(v.target.value)}
                onPressEnter={(e) => onPressEnter()}
                onSearch={(value) => onSearch(value)}
                allowClear={{
                    clearIcon: <CloseCircleFilled/>
                }}
            />
        </Popover>
    )
}